/**
 * DocumentStage — the active document's zoomable viewport.
 *
 * Zoom model: a single `zoom` number where 1 = fit-width; the image is rendered
 * at `width: zoom * 100%` inside an `overflow: auto` container, so PAN is the
 * container's native scroll (plain wheel/trackpad scrolls the tall document —
 * the behavior reviewers expect — and is never intercepted). On top of that:
 *
 * - +/− buttons and keyboard `+` `-` `0` (viewport focused) step ×1.25.
 * - Ctrl/Cmd + wheel zooms to the cursor (attached non-passively via ref —
 *   React's onWheel cannot preventDefault).
 * - Drag-to-pan with pointer capture (mouse only; touch keeps native panning).
 * - "Ancho" = fit-width (zoom 1) · "100 %" = natural pixel size.
 *
 * `DocumentRef.url` is OPAQUE (signed URL / local byte route) — used verbatim.
 * Load failures (expired signed URL) show the inline retry card; the parent
 * keys this component by `${object_name}:${url}` so a reload with fresh URLs
 * remounts and resets both `failed` and `zoom`.
 *
 * PDFs (a real share of real rounds — the worker rasterizes them for Gemini
 * but the bucket keeps the original) can't render in an `<img>`; they get the
 * browser's native PDF viewer in an `<iframe>` instead, which brings its own
 * zoom/pan, so the toolbar and drag model don't apply.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";

import type { DocumentRef } from "../../api/client";

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 6;
const ZOOM_STEP = 1.25;

// Viewport-derived so the whole workbench card (header + toolbar + stage +
// thumbnail strip) fits under the sticky topbar when the left column is
// pinned at lg. ~330px = topbar + card chrome.
const STAGE_HEIGHT = "clamp(360px, calc(100vh - 330px), 900px)";

export function isPdfDocument(doc: DocumentRef): boolean {
  return (
    doc.content_type === "application/pdf" ||
    doc.object_name.toLowerCase().endsWith(".pdf")
  );
}

function clampZoom(z: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

export function DocumentStage({
  doc,
  label,
  onRetry,
}: {
  doc: DocumentRef;
  label: string;
  onRetry?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const naturalRef = useRef<{ w: number; h: number } | null>(null);
  /** Scroll correction to apply right after the zoom re-render (zoom-to-point). */
  const pendingRef = useRef<{ ratio: number; px: number; py: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);

  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [failed, setFailed] = useState(false);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  /** Zoom keeping the given viewport point (client coords) fixed. */
  const zoomTo = useCallback((next: number, clientX?: number, clientY?: number) => {
    const el = containerRef.current;
    const clamped = clampZoom(next);
    if (el && clamped !== zoomRef.current) {
      const rect = el.getBoundingClientRect();
      pendingRef.current = {
        ratio: clamped / zoomRef.current,
        px: (clientX ?? rect.left + rect.width / 2) - rect.left,
        py: (clientY ?? rect.top + rect.height / 2) - rect.top,
      };
    }
    setZoom(clamped);
  }, []);

  // Apply the zoom-to-point scroll correction after the width change lands.
  useLayoutEffect(() => {
    const el = containerRef.current;
    const p = pendingRef.current;
    if (el && p) {
      el.scrollLeft = (el.scrollLeft + p.px) * p.ratio - p.px;
      el.scrollTop = (el.scrollTop + p.py) * p.ratio - p.py;
      pendingRef.current = null;
    }
  }, [zoom]);

  // Ctrl/Cmd + wheel → zoom to cursor. Non-passive so preventDefault works;
  // plain wheel is never intercepted (native scroll = pan).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomTo(zoomRef.current * factor, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomTo, failed]);

  /** "100 %": natural pixel size (zoom = naturalW / container width). */
  const zoomNatural = useCallback(() => {
    const el = containerRef.current;
    const nat = naturalRef.current;
    if (!el || !nat || el.clientWidth === 0) return;
    zoomTo(nat.w / el.clientWidth);
  }, [zoomTo]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Mouse only: touch keeps native scroll-panning via touchAction.
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const el = containerRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    setDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    const drag = dragRef.current;
    if (!el || !drag) return;
    el.scrollLeft = drag.sl - (e.clientX - drag.x);
    el.scrollTop = drag.st - (e.clientY - drag.y);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    containerRef.current?.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomTo(zoomRef.current * ZOOM_STEP);
    } else if (e.key === "-") {
      e.preventDefault();
      zoomTo(zoomRef.current / ZOOM_STEP);
    } else if (e.key === "0") {
      e.preventDefault();
      zoomTo(1);
    }
    // Arrows: native container scroll — left free on purpose.
  };

  if (isPdfDocument(doc)) {
    // Native PDF viewer (own zoom/scroll UI); the workbench header's
    // "Abrir en pestaña nueva" link covers browsers without an inline viewer.
    return (
      <Box
        component="iframe"
        src={doc.url}
        title={label}
        sx={{
          display: "block",
          width: "100%",
          height: STAGE_HEIGHT,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "grey.100",
        }}
      />
    );
  }

  if (failed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 8,
          color: "text.secondary",
          bgcolor: "grey.50",
          borderRadius: 1,
        }}
      >
        <BrokenImageOutlinedIcon />
        <Typography variant="caption">
          No se pudo mostrar el documento. El enlace pudo haber expirado o el
          formato no es visualizable en el navegador.
        </Typography>
        {onRetry ? (
          <Link component="button" variant="caption" onClick={onRetry}>
            Reintentar
          </Link>
        ) : (
          <Typography variant="caption">Recarga la página.</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Zoom toolbar */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", justifyContent: "flex-end", mb: 1 }}
      >
        <Tooltip title="Alejar ( − )">
          <IconButton size="small" aria-label="Alejar" onClick={() => zoomTo(zoom / ZOOM_STEP)}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Acercar ( + )">
          <IconButton size="small" aria-label="Acercar" onClick={() => zoomTo(zoom * ZOOM_STEP)}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" onClick={() => zoomTo(1)} sx={{ minWidth: 0 }}>
          Ancho
        </Button>
        <Button size="small" onClick={zoomNatural} sx={{ minWidth: 0 }}>
          100&nbsp;%
        </Button>
      </Stack>

      {/* Viewport: native scroll = pan; Ctrl/Cmd+wheel = zoom; drag = pan. */}
      <Box
        ref={containerRef}
        tabIndex={0}
        role="group"
        aria-label={`Visor de documento: ${label}. Use +, − y 0 para el zoom; desplácese para navegar.`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        sx={{
          overflow: "auto",
          height: STAGE_HEIGHT,
          bgcolor: "grey.100",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "pan-x pan-y",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.light",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="img"
          // url is OPAQUE — used verbatim, never parsed/rebuilt.
          src={doc.url}
          alt={label}
          draggable={false}
          onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
            naturalRef.current = {
              w: e.currentTarget.naturalWidth,
              h: e.currentTarget.naturalHeight,
            };
          }}
          onError={() => setFailed(true)}
          sx={{
            display: "block",
            width: `${zoom * 100}%`,
            height: "auto",
            userSelect: "none",
          }}
        />
      </Box>
    </Box>
  );
}
